import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, MapPin, Clock, Calendar, Wallet, Shield,
    Star, Check, ArrowRight, Share2, Heart, ChevronLeft,
} from 'lucide-react';
import axios from 'axios';

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

function formatDate(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatTime(dt) {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(dt) {
    if (!dt) return '';
    const mins = Math.floor((Date.now() - new Date(dt)) / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function posterName(job) {
    const u = job.createdBy;
    if (!u) return 'Unknown';
    const f = u.firstname || u.firstName || '';
    const l = u.lastname  || u.lastName  || '';
    return (f || l) ? `${f} ${l}`.trim() : (u.username || 'Unknown');
}

function avatarInitials(name) {
    return name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function parseTags(str) {
    return str ? str.split(',').map(t => t.trim()).filter(Boolean) : [];
}

function parseReqs(str) {
    return str ? str.split('||').map(r => r.trim()).filter(Boolean) : [];
}

// ── Fact cell ────────────────────────────────────────────────────────────────

function Fact({ icon: Icon, label, value, sub }) {
    return (
        <div className="jd-fact">
            <span className="jd-fact-icon"><Icon size={16} /></span>
            <div className="jd-fact-text">
                <div className="jd-fact-label">{label}</div>
                <div className="jd-fact-value">{value}</div>
                {sub && <div className="jd-fact-sub">{sub}</div>}
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

const JobInfo = () => {
    const { jobId } = useParams();
    const navigate  = useNavigate();

    const [job, setJob]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]   = useState('');
    const [applying, setApplying] = useState(false);
    const [applied, setApplied]   = useState(false);
    const [applyError, setApplyError] = useState('');
    const [saved, setSaved]   = useState(false);

    useEffect(() => {
        const token = getToken();
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        .then(r => setJob(r.data))
        .catch(e => setError(e.response?.status === 404 ? 'Job not found.' : 'Failed to load job.'))
        .finally(() => setLoading(false));
    }, [jobId]);

    const handleApply = async () => {
        const token = getToken();
        if (!token) { navigate('/login'); return; }
        setApplying(true);
        setApplyError('');
        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}/apply`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setApplied(true);
        } catch (e) {
            const msg = e.response?.data;
            setApplyError(typeof msg === 'string' ? msg : 'Failed to submit application.');
        } finally {
            setApplying(false);
        }
    };

    const handleShare = () => {
        const url = window.location.href;
        if (navigator.share) navigator.share({ title: job?.title, url }).catch(() => {});
        else navigator.clipboard?.writeText(url);
    };

    if (loading) return <div className="jd-loading">Loading job details…</div>;
    if (error)   return <div className="jd-loading" style={{ color: 'var(--coral)' }}>{error}</div>;
    if (!job)    return null;

    const tags   = parseTags(job.tags);
    const reqs   = parseReqs(job.requirements);
    const name   = posterName(job);
    const ini    = avatarInitials(name);
    const isVerified = !!job.createdBy?.passportUrl;

    return (
        <div className="jd-page">
            <div className="jd-page-inner">

                {/* ── Back bar ── */}
                <div className="jd-back-bar">
                    <button className="jd-back-btn" onClick={() => navigate(-1)}>
                        <ChevronLeft size={16} /> Back to jobs
                    </button>
                    <div className="jd-topbar-chips">
                        {tags[0] && <span className="ml-badge ml-badge-active">{tags[0]}</span>}
                        {job.verifiedOnly && (
                            <span className="ml-badge ml-badge-active">
                                <Star size={10} /> Verified only
                            </span>
                        )}
                        {job.createdAt && (
                            <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>
                                Posted {timeAgo(job.createdAt)}
                            </span>
                        )}
                        <button className="jd-back-btn" style={{ marginLeft: 4 }} onClick={() => setSaved(s => !s)} aria-label="Save">
                            <Heart size={15} fill={saved ? 'currentColor' : 'none'} />
                        </button>
                        <button className="jd-back-btn" onClick={handleShare} aria-label="Share">
                            <Share2 size={15} />
                        </button>
                    </div>
                </div>

                {/* ── Main grid ── */}
                <div className="jd-grid" style={{ gap: '32px' }}>

                    {/* Left: content */}
                    <main className="jd-main" style={{ padding: 0 }}>
                        <h1 className="jd-title">{job.title}</h1>

                        {/* Facts */}
                        <div className="jd-facts">
                            <Fact icon={MapPin}   label="Where"    value={job.address || '—'} />
                            <Fact icon={Clock}    label="When"     value={formatDate(job.dateTime)}
                                                   sub={formatTime(job.dateTime)} />
                            {job.duration && (
                                <Fact icon={Calendar} label="Duration" value={job.duration} />
                            )}
                            {job.flex && (
                                <Fact icon={Clock} label="Flexibility" value={`± ${job.flex}`} />
                            )}
                        </div>

                        {/* Description */}
                        {job.description && (
                            <section className="jd-section">
                                <h3 className="jd-section-title">About this job</h3>
                                <p className="jd-desc">{job.description}</p>
                            </section>
                        )}

                        {/* Requirements */}
                        {reqs.length > 0 && (
                            <section className="jd-section">
                                <h3 className="jd-section-title">Requirements</h3>
                                <ul className="jd-reqs">
                                    {reqs.map((r, i) => (
                                        <li key={i}>
                                            <span className="jd-req-check"><Check size={12} /></span>
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Tags */}
                        {tags.length > 0 && (
                            <section className="jd-section">
                                <h3 className="jd-section-title">Tags</h3>
                                <div className="jd-tags">
                                    {tags.map(t => (
                                        <span key={t} className="ml-tag">#{t}</span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Poster */}
                        <section className="jd-section">
                            <h3 className="jd-section-title">Posted by</h3>
                            <div className="jd-poster-card">
                                <div className="jd-poster-avatar"
                                    style={{ background: 'var(--teal-700)' }}>
                                    {ini}
                                </div>
                                <div className="jd-poster-meta">
                                    <div className="jd-poster-name">
                                        {name}
                                        {isVerified && (
                                            <span className="jd-verified-pill">
                                                <Shield size={9} /> Verified
                                            </span>
                                        )}
                                    </div>
                                    <div className="jd-poster-stats">
                                        <span><Star size={11} /> 4.9</span>
                                        <span>·</span>
                                        <span>Replies in ~12 min</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Safety note */}
                        <div className="jd-safety">
                            <Shield size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                            <div>
                                <div className="jd-safety-title">Interim covers payment until the job is done.</div>
                                <div className="jd-safety-body">You only get paid once both sides mark it complete. No chasing cash.</div>
                            </div>
                        </div>
                    </main>

                    {/* Right: sticky sidebar */}
                    <aside className="jd-side" style={{ borderLeft: '1px solid var(--ink-200)', padding: '0 0 0 32px', background: 'none', position: 'sticky', top: '88px' }}>
                        <div className="jd-pay-card">
                            <div className="jd-pay-eyebrow">Total payment</div>
                            <div className="jd-pay-amount">€{job.payment?.toFixed(0) ?? '—'}</div>
                            <div className="jd-pay-meta">
                                Fixed price{job.duration ? ` · ${job.duration}` : ''}
                            </div>

                            <div className="jd-pay-divider" />

                            {applied ? (
                                <div className="jd-applied-banner" style={{ background: 'color-mix(in srgb,var(--teal-500) 22%,transparent)', color: 'var(--white)', border: 'none' }}>
                                    <Check size={16} /> Application submitted!
                                </div>
                            ) : (
                                <>
                                    <button
                                        className="ix-btn ix-btn-lg jd-apply-btn"
                                        onClick={handleApply}
                                        disabled={applying}
                                    >
                                        {applying ? 'Submitting…' : <><span>Apply for this job</span> <ArrowRight size={16} /></>}
                                    </button>
                                    {applyError && (
                                        <div style={{ fontSize: '12px', color: 'var(--coral)', marginTop: '8px', textAlign: 'center' }}>
                                            {applyError}
                                        </div>
                                    )}
                                    <div className="jd-pay-foot">Free to apply · No fees for workers</div>
                                </>
                            )}
                        </div>

                        <div className="jd-side-card">
                            <div className="jd-side-row">
                                <span className="jd-side-row-l"><Wallet size={14} /> Payment</span>
                                <span className="mono" style={{ fontSize: '12px', color: 'var(--ink-950)' }}>€{job.payment?.toFixed(2) ?? '—'}</span>
                            </div>
                            {job.duration && (
                                <div className="jd-side-row">
                                    <span className="jd-side-row-l"><Clock size={14} /> Duration</span>
                                    <span className="mono" style={{ fontSize: '12px', color: 'var(--ink-950)' }}>{job.duration}</span>
                                </div>
                            )}
                            <div className="jd-side-row">
                                <span className="jd-side-row-l"><Shield size={14} /> Verified only</span>
                                <span className="mono" style={{ fontSize: '12px', color: 'var(--ink-950)' }}>{job.verifiedOnly ? 'Yes' : 'No'}</span>
                            </div>
                            {job.flex && (
                                <div className="jd-side-row">
                                    <span className="jd-side-row-l"><Clock size={14} /> Flexibility</span>
                                    <span className="mono" style={{ fontSize: '12px', color: 'var(--ink-950)' }}>± {job.flex}</span>
                                </div>
                            )}
                        </div>

                        <button className="jd-report">
                            Report this listing
                        </button>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default JobInfo;
